/** Email Center helpers: pure predicates and row-building for the Templates tab. */
import {
  AutomationConditionModel,
  EmailDriver,
  EmailTemplateModel,
  EmailTemplateTypeModel,
  JourneyThemeModel,
  TemplateCampaignUsageModel,
} from '@/typings/api/emailCenter';

/**
 * A template still authored for Newsletter2Go.
 *
 * NL2Go names its placeholders differently, so our variable paths match nothing
 * in one of its bodies: offering to fill them from a real lead would produce a
 * preview that silently changes nothing. The visual editor is not gaining NL2Go
 * support — the provider is being retired — so the tooling is withheld instead.
 *
 * Absent (older payloads) counts as Mailcoach: withholding the feature from a
 * template we cannot classify would be the more disruptive guess.
 */
export function isNl2goTemplate(
  template?: Pick<EmailTemplateModel, 'target_provider'> | null,
): boolean {
  return template?.target_provider === 'nl2go';
}

/**
 * Whether a template can be sent by a journey running `driver`.
 *
 * A journey sends through exactly one provider — its `email_driver` — so a
 * template authored for the other one is not something that lead source could
 * ever send, and offering it (to link, to edit, as "unused") only invites a
 * setup that silently never goes out.
 *
 * Everything unknown passes. The driver is null until the journey's email
 * config lands, `'null'` for a journey with no provider resolved at all, and
 * `target_provider` is absent on older template payloads — none of those are an
 * answer, and hiding a partner's templates on a missing one is the one outcome
 * worse than showing too many.
 */
export function templateMatchesEmailDriver(
  template: Pick<EmailTemplateModel, 'target_provider'>,
  driver: EmailDriver | null,
): boolean {
  if (driver !== 'mailcoach' && driver !== 'nl2go') return true;
  if (!template.target_provider) return true;
  return template.target_provider === driver;
}

/** 'custom' = partner-owned template, 'master' = shared (non-owned public). */
export type TriggerTemplateSource = 'custom' | 'master';

export interface TriggerCoverageRow {
  /** Stable table row key — a trigger can map to several templates. */
  rowKey: string;
  triggerKey: string;
  triggerLabel: string;
  template: EmailTemplateModel | null;
  source: TriggerTemplateSource | null;
}

function coverageRow(
  triggerKey: string,
  triggerLabel: string,
  template: EmailTemplateModel | null,
): TriggerCoverageRow {
  return {
    rowKey: `${triggerKey}:${template?.id ?? 'none'}`,
    triggerKey,
    triggerLabel,
    template,
    source: template ? (template.is_owned ? 'custom' : 'master') : null,
  };
}

/**
 * The trigger-centric Templates view: one row per (trigger, matching
 * template) pair so every template stays visible and actionable, plus one
 * uncovered row per trigger with no template — that row is where a template
 * gets created. Owned templates sort before shared masters, then newest
 * first. Templates whose email_type matches no known trigger are appended
 * (raw key as label) rather than silently hidden.
 */
export function buildTriggerCoverage(
  triggers: EmailTemplateTypeModel[],
  templates: EmailTemplateModel[],
): TriggerCoverageRow[] {
  const byTrigger = new Map<string, EmailTemplateModel[]>();
  for (const template of templates) {
    const key = template.email_type || '';
    const bucket = byTrigger.get(key);
    if (bucket) bucket.push(template);
    else byTrigger.set(key, [template]);
  }

  const rank = (template: EmailTemplateModel) => (template.is_owned ? 0 : 1);
  const sortBucket = (bucket: EmailTemplateModel[]) =>
    [...bucket].sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (b.updated_at || '').localeCompare(a.updated_at || ''),
    );

  const rows: TriggerCoverageRow[] = [];
  const knownKeys = new Set<string>();

  for (const trigger of triggers) {
    knownKeys.add(trigger.key);
    const bucket = byTrigger.get(trigger.key);
    if (!bucket) {
      rows.push(coverageRow(trigger.key, trigger.label, null));
      continue;
    }
    for (const template of sortBucket(bucket))
      rows.push(coverageRow(trigger.key, trigger.label, template));
  }

  for (const [key, bucket] of byTrigger) {
    if (knownKeys.has(key)) continue;
    for (const template of sortBucket(bucket))
      rows.push(coverageRow(key, key, template));
  }

  return rows;
}

/**
 * Whether an event type is configured per lead source.
 *
 * The one place that reads the backend's `journey_specific` flag, because the
 * absent case has to mean "journey-specific": that is what every type was before
 * the flag existed, and a type whose picker silently disappeared would be a worse
 * failure than one that offers a scope it does not need.
 */
export function isJourneySpecificType(
  types: EmailTemplateTypeModel[],
  emailType: string | null | undefined,
): boolean {
  // No trigger at all — a marketing template, which no lead source scopes.
  if (!emailType) return true;
  return (
    types.find((type) => type.key === emailType)?.journey_specific !== false
  );
}

/**
 * Whether a trigger type can still take another template.
 *
 * Per lead source for an ordinary type: it is covered once *every* source has one,
 * since a source without its own template still needs the default journey's. A
 * journey-independent type is covered as soon as one template exists — there is
 * only ever one.
 */
export function isTypeFullyCovered(
  type: EmailTemplateTypeModel,
  coveredJourneyIds: Set<string> | undefined,
  journeyHashIds: string[],
): boolean {
  if (type.journey_specific === false) {
    return (coveredJourneyIds?.size ?? 0) > 0;
  }
  return (
    journeyHashIds.length > 0 &&
    coveredJourneyIds != null &&
    journeyHashIds.every((hashId) => coveredJourneyIds.has(hashId))
  );
}

/** Shared journey context of a configured-email row (tag + ordering). */
interface ConfiguredRowJourney {
  journeyHashId: string;
  journeyName: string;
  isDefaultJourney: boolean;
}

export interface ConfiguredTransactionalRow extends ConfiguredRowJourney {
  rowKey: string;
  emailType: string;
  /** Resolved from the template types; falls back to the raw key. */
  triggerLabel: string;
  /**
   * False ⇒ this email exists once for the account. The row then drops its
   * journey tag: "Default" describes a choice, and there was none to make.
   */
  journeySpecific: boolean;
  language: string | null;
  isEnabled: boolean;
  template: EmailTemplateModel;
}

export interface ConfiguredMarketingRow extends ConfiguredRowJourney {
  rowKey: string;
  /**
   * What configures this send, which is also what the row can say about it:
   *
   * - `automation` — a Mailcoach automation step: it has a wait and conditions.
   * - `campaign` — an NL2Go campaign trigger: no wait and no conditions of a
   *   shape we can read, so `triggerName` carries the schedule instead.
   * - `link` — a bare journey link, how legacy drip mails were migrated: no
   *   schedule at all, only whether it is enabled.
   */
  source: 'automation' | 'campaign' | 'link';
  /** The automation's name, or the campaign's. Empty for a bare link. */
  automationName: string;
  /**
   * Every campaign firing this mail on this journey, deduplicated. One name in
   * all but the rare case of a mail two campaigns share; empty on the other two
   * sources.
   */
  campaignNames: string[];
  /**
   * The NL2Go trigger's name ("Lieferzeit Schnellstmöglich - 7 Tage"), which is
   * where such a campaign states when it sends. Only when exactly one trigger
   * fires the mail here — with several there is no single answer, and
   * `triggerCount` is what the row shows instead. Null on the other two sources.
   */
  triggerName: string | null;
  /** How many campaign triggers fire this mail on this journey; 0 otherwise. */
  triggerCount: number;
  automationStatus: 'active' | 'inactive';
  /**
   * Whether this send is switched on, when it is a switch this page owns.
   *
   * Only campaign rows have one: an NL2Go campaign mail is enabled per lead
   * source (`journey_triggers.is_enabled`), and that is the single decision the
   * partner makes about a mail whose content and schedule the campaign owns.
   * Null on the other two sources — an automation step's on/off is the
   * automation's, and a bare journey link has no journey-level control here —
   * which is what keeps a switch off rows that could not honour it.
   */
  isEnabled: boolean | null;
  waitInDays: number;
  /**
   * The step's send conditions, in Mailcoach's evaluation order — see
   * `automationConditions.ts` for what they mean and how they are worded.
   */
  conditions: AutomationConditionModel[];
  template: EmailTemplateModel;
}

/**
 * Which of the two pools a template belongs to: an automation/marketing mail,
 * or a journey-triggered transactional one.
 *
 * The very rule the API splits on — `EmailTemplate::scopeOfType` reads
 * `email_type = ''` as marketing and anything else as transactional — so this
 * answers "which of the two GET /email-templates calls returned it" without
 * having to carry the pool it came from alongside the row.
 */
export function isMarketingTemplate(
  template: Pick<EmailTemplateModel, 'email_type'>,
): boolean {
  return !template.email_type;
}

export interface ConfiguredEmailRows {
  transactional: ConfiguredTransactionalRow[];
  marketing: ConfiguredMarketingRow[];
}

/**
 * The Templates tab's configured-email rows, straight from the template
 * pool's embedded usage (GET /email-templates ships `journeys` /
 * `automations` per template): one transactional row per journey link, one
 * marketing row per automation step. The default journey sorts first (its
 * mails are what other journeys inherit), then journeys by name; within a
 * journey, transactional rows follow the template-type catalog order and
 * marketing rows the automation name + step delay.
 *
 * **Which section a template lands in is its kind** (`isMarketingTemplate`),
 * not whichever usage array happens to be filled. The pool is fetched as two
 * lists for exactly that reason, and reading the usage instead let both of them
 * pile into the transactional section: a marketing mail carrying a journey link
 * was listed as a journey-triggered email under an empty trigger label, which
 * is an email nothing sends that way. So a marketing template contributes only
 * automation rows — one per step, or, when no step references it, one per
 * journey link, which is how legacy drip mails are configured — and a
 * transactional one only journey rows. Every row names the journey it belongs
 * to, which is what lets the scope bar answer "what does *this* journey send".
 * Campaign usage collapses to one row per journey however many triggers fire
 * it — see below.
 * A template contributing no row surfaces as unused (`isTemplateInUse` splits
 * the same way, so neither list can swallow it).
 */
export function buildConfiguredEmailRows(
  templates: EmailTemplateModel[],
  types: EmailTemplateTypeModel[],
): ConfiguredEmailRows {
  const typeOrder = new Map(types.map((type, index) => [type.key, index]));
  const typeLabels = new Map(types.map((type) => [type.key, type.label]));

  const transactional: ConfiguredTransactionalRow[] = [];
  const marketing: ConfiguredMarketingRow[] = [];

  for (const template of templates) {
    if (isMarketingTemplate(template)) {
      const steps = template.automations ?? [];

      steps.forEach((usage, index) => {
        marketing.push({
          rowKey: `${template.id}:automation:${usage.journey_id}:${index}`,
          journeyHashId: usage.journey_id,
          journeyName: usage.journey_name,
          isDefaultJourney: usage.is_default,
          source: 'automation',
          automationName: usage.automation_name ?? '',
          campaignNames: [],
          triggerName: null,
          triggerCount: 0,
          automationStatus: usage.status,
          isEnabled: null,
          waitInDays: usage.wait_in_days,
          conditions: usage.conditions ?? [],
          template,
        });
      });

      /**
       * The NL2Go side of the same section: a campaign trigger is what sends a
       * marketing mail for a journey still on that provider, and it is the only
       * configuration such a mail has. Without it, every NL2Go campaign mail
       * reached the page as a template nothing uses.
       *
       * **One row per journey, not per trigger.** A campaign fires the same mail
       * from as many triggers as it has audience slices — the Sachtipps mails
       * run to four apiece — and a row each turned one email into four identical
       * lines that differ only in a name. The row says how many there are; the
       * triggers themselves, with the conditions they fire on, are in the
       * template's own view (`TemplateDrawer`), which is where a detail nothing
       * on this page can act on belongs.
       */
      const campaignSteps = template.campaigns ?? [];
      const usagesByJourney = new Map<string, TemplateCampaignUsageModel[]>();
      for (const usage of campaignSteps) {
        const existing = usagesByJourney.get(usage.journey_id);
        if (existing) {
          existing.push(usage);
        } else {
          usagesByJourney.set(usage.journey_id, [usage]);
        }
      }

      for (const [journeyHashId, usages] of usagesByJourney) {
        const campaignNames = [
          ...new Set(
            usages
              .map((usage) => usage.campaign_name)
              .filter((name): name is string => Boolean(name)),
          ),
        ];

        marketing.push({
          rowKey: `${template.id}:campaign:${journeyHashId}`,
          journeyHashId,
          journeyName: usages[0].journey_name,
          isDefaultJourney: usages[0].is_default,
          source: 'campaign',
          // One campaign in all but the rare shared mail; naming one of two
          // would be picking a side, so the row falls back to the count.
          automationName: campaignNames.length === 1 ? campaignNames[0] : '',
          campaignNames,
          triggerName: usages.length === 1 ? usages[0].trigger_name : null,
          triggerCount: usages.length,
          /**
           * One live trigger is enough for the mail to go out; all of them
           * unpushed, or the journey's switch off, is what makes the whole
           * configuration inactive.
           *
           * The switch is folded in because the row states whether the mail
           * sends, and a disabled one does not however many triggers point at
           * it — the switch beside the tag then says why.
           */
          automationStatus: usages.some(
            (usage) => usage.status === 'active' && usage.is_enabled !== false,
          )
            ? 'active'
            : 'inactive',
          /**
           * Absent counts as enabled (older payloads), and one enabled trigger
           * is enough: the switch writes every trigger of this (journey, mail)
           * pair at once, so they only ever disagree if something outside the
           * Email Center wrote one of them.
           */
          isEnabled: usages.some((usage) => usage.is_enabled !== false),
          // An NL2Go trigger's delay lives inside its filter expression, not in
          // a field — quoting a wait of 0 here would read as "immediately".
          waitInDays: 0,
          conditions: [],
          template,
        });
      }

      /**
       * No step references it — the shape legacy drip mails were migrated in,
       * where the journey links the mail directly. It is an automation mail all
       * the same (that is what having no event type means), so it belongs in
       * this list rather than under "Unused templates".
       *
       * One row per journey link, because a row without a journey is a row the
       * scope bar cannot place: it would then show under every journey, which
       * is exactly what "these automation mails are always listed" was. With no
       * step there is no name, wait or condition to show, and the link's own
       * enabled flag is the only status there is.
       */
      if (steps.length === 0 && campaignSteps.length === 0) {
        (template.journeys ?? []).forEach((usage, index) => {
          marketing.push({
            rowKey: `${template.id}:link:${usage.journey_id}:${index}`,
            journeyHashId: usage.journey_id,
            journeyName: usage.journey_name,
            isDefaultJourney: usage.is_default,
            source: 'link',
            automationName: '',
            campaignNames: [],
            triggerName: null,
            triggerCount: 0,
            automationStatus: usage.is_enabled ? 'active' : 'inactive',
            isEnabled: null,
            waitInDays: 0,
            conditions: [],
            template,
          });
        });
      }
      continue;
    }

    (template.journeys ?? []).forEach((usage, index) => {
      transactional.push({
        rowKey: `${template.id}:link:${usage.journey_id}:${index}`,
        journeyHashId: usage.journey_id,
        journeyName: usage.journey_name,
        isDefaultJourney: usage.is_default,
        emailType: template.email_type,
        triggerLabel:
          typeLabels.get(template.email_type) ?? template.email_type,
        journeySpecific: isJourneySpecificType(types, template.email_type),
        language: usage.language,
        isEnabled: usage.is_enabled,
        template,
      });
    });
  }

  const byJourney = (a: ConfiguredRowJourney, b: ConfiguredRowJourney) =>
    Number(b.isDefaultJourney) - Number(a.isDefaultJourney) ||
    (a.journeyName || '').localeCompare(b.journeyName || '');

  transactional.sort(
    (a, b) =>
      byJourney(a, b) ||
      (typeOrder.get(a.emailType) ?? types.length) -
        (typeOrder.get(b.emailType) ?? types.length) ||
      a.emailType.localeCompare(b.emailType),
  );
  marketing.sort(
    (a, b) =>
      byJourney(a, b) ||
      a.automationName.localeCompare(b.automationName) ||
      a.waitInDays - b.waitInDays ||
      (a.triggerName ?? '').localeCompare(b.triggerName ?? ''),
  );

  return { transactional, marketing };
}

/** One trigger of a campaign, and the mail it sends. */
export interface CampaignTriggerRow {
  rowKey: string;
  triggerName: string | null;
  status: 'active' | 'inactive';
  /**
   * Whether the lead source has this mail switched on. False makes an otherwise
   * live trigger send nothing, so the row has to say so — the switch itself is
   * on the Templates tab, where the mail is listed as a configured email.
   */
  isEnabled: boolean;
  /** The filter clauses, verbatim — see `TemplateCampaignUsageModel.conditions`. */
  conditions: string[];
  template: EmailTemplateModel;
}

/** One NL2Go campaign as it runs on a single journey. */
export interface JourneyCampaign {
  /** The campaign's hash id, or its name when the trigger names no campaign. */
  key: string;
  campaignName: string | null;
  triggers: CampaignTriggerRow[];
}

/**
 * What a journey's NL2Go campaigns send, grouped the way they are configured:
 * campaign → trigger → mail.
 *
 * The pool is the source because that is where the data already is — the
 * template list embeds every campaign trigger on the caller's journeys
 * (`campaigns[]`), so this needs no request of its own. Inverted, though: the
 * list is keyed by template and a campaign is what a reader thinks in, since
 * one campaign runs many mails and one mail appears in many of its triggers.
 *
 * Grouped by `campaign_id`, not by name: names repeat, and two campaigns merged
 * under one heading would misstate what sends what. Campaigns sort by name and
 * their triggers by their own, which is how the legacy admin lists them —
 * trigger names carry the schedule ("… - 100 Tage"), so this is not alphabetical
 * noise but roughly the order they fire in.
 */
export function buildJourneyCampaigns(
  templates: EmailTemplateModel[],
  journeyHashId: string | null,
): JourneyCampaign[] {
  if (!journeyHashId) return [];

  const campaigns = new Map<string, JourneyCampaign>();

  for (const template of templates) {
    for (const usage of template.campaigns ?? []) {
      if (usage.journey_id !== journeyHashId) continue;

      // A trigger whose campaign row is gone still fires; its name is then the
      // only handle there is, and an empty one still groups those together.
      const key = usage.campaign_id ?? usage.campaign_name ?? '';
      let campaign = campaigns.get(key);
      if (!campaign) {
        campaign = {
          key,
          campaignName: usage.campaign_name ?? null,
          triggers: [],
        };
        campaigns.set(key, campaign);
      }

      campaign.triggers.push({
        rowKey: `${key}:${template.id}:${campaign.triggers.length}`,
        triggerName: usage.trigger_name ?? null,
        status: usage.status,
        isEnabled: usage.is_enabled !== false,
        conditions: usage.conditions ?? [],
        template,
      });
    }
  }

  const byName = (a: string | null, b: string | null) =>
    (a ?? '').localeCompare(b ?? '');

  return [...campaigns.values()]
    .map((campaign) => ({
      ...campaign,
      triggers: [...campaign.triggers].sort(
        (a, b) =>
          byName(a.triggerName, b.triggerName) ||
          a.template.name.localeCompare(b.template.name),
      ),
    }))
    .sort((a, b) => byName(a.campaignName, b.campaignName));
}

/**
 * The trigger types **one lead source already sends** — what closes an event off
 * for creating.
 *
 * Scoped the way `scopeConfiguredEmailRows` scopes the listing, and for the same
 * reason: a journey sends its own links, plus the default journey's for every
 * trigger it has none of its own for. A link on some *other* lead source is none
 * of this journey's business — it neither sends that template nor inherits it, so
 * the event is still free here and offering it closed off would leave a lead
 * source with no way to configure an email it does not have.
 *
 * `null` (no scope resolved yet) keeps the cross-journey union, matching the
 * unscoped listing it describes.
 */
export function scopedCoveredEmailTypes(
  templates: EmailTemplateModel[],
  journeyHashId: string | null,
): Set<string> {
  const covered = new Set<string>();
  for (const template of templates) {
    if (!template.email_type) continue;
    for (const usage of template.journeys ?? []) {
      if (
        journeyHashId == null ||
        usage.journey_id === journeyHashId ||
        // The inheritance source: a trigger the default has is a trigger every
        // follower sends, so it is covered on this journey too.
        usage.is_default
      ) {
        covered.add(template.email_type);
        break;
      }
    }
  }
  return covered;
}

/**
 * Journey hash-ids that already have a template linked, per email type.
 *
 * Built from the usage the template list embeds (`journeys`), so "which lead
 * sources already send this event" needs no per-journey fetch. A template
 * that exists but is linked nowhere covers nothing — coverage is about what
 * a journey would actually send, not about what is in the pool.
 */
export function buildJourneyCoverageByType(
  templates: EmailTemplateModel[],
): Map<string, Set<string>> {
  const covered = new Map<string, Set<string>>();
  for (const template of templates) {
    if (!template.email_type) continue;
    for (const usage of template.journeys ?? []) {
      let journeyIds = covered.get(template.email_type);
      if (!journeyIds) {
        journeyIds = new Set();
        covered.set(template.email_type, journeyIds);
      }
      journeyIds.add(usage.journey_id);
    }
  }
  return covered;
}

/**
 * True when a journey is configured to send the template: a journey link for a
 * transactional one, and for an automation mail any of the three ways one is
 * configured — a Mailcoach automation step, an NL2Go campaign trigger, or a
 * bare journey link (how legacy drip mails were migrated).
 *
 * The complement of `buildConfiguredEmailRows`, which lists exactly the same
 * templates: whatever it builds no row for is what the Templates tab shows as
 * "unused", so the two rules have to agree or a template ends up on the page
 * nowhere at all — neither configured nor unused, and no longer editable or
 * deletable.
 */
export function isTemplateInUse(template: EmailTemplateModel): boolean {
  return isMarketingTemplate(template)
    ? (template.automations?.length ?? 0) > 0 ||
        (template.campaigns?.length ?? 0) > 0 ||
        (template.journeys?.length ?? 0) > 0
    : (template.journeys?.length ?? 0) > 0;
}

/**
 * Whether an automation sends this template — an "automation mail".
 *
 * These are **read-only in the Email Center**: what goes out and when is the
 * automation's, not the template's, and the two would disagree the moment one
 * of them is edited alone (a step's Mailcoach mail is provisioned from the
 * template at save time, see `MailcoachTemplateProvisioner::syncAutomationMail`).
 * So the page shows them, in full, with View instead of Edit — one rule, used by
 * the row actions and by the editor drawer, so a deep link cannot slip past it.
 */
export function isAutomationMail(
  template: Pick<EmailTemplateModel, 'automations'>,
): boolean {
  return (template.automations?.length ?? 0) > 0;
}

/**
 * Whether a campaign trigger sends this template — the NL2Go counterpart of
 * {@link isAutomationMail}. Detaching one is blocked: a campaign mail hangs off
 * a shared `email_triggers` row, not `journey_email_templates`, so the clone
 * would be a copy nothing sends.
 */
export function isCampaignMail(
  template: Pick<EmailTemplateModel, 'campaigns'>,
): boolean {
  return (template.campaigns?.length ?? 0) > 0;
}

/**
 * Flattens a journey theme into the `{{ path }}` → value map the preview
 * pane's naive substitution understands ("theme.colors.brand" → "#e20015").
 * Only string leaves are exposed; the emails' server-side Twig payload carries
 * the same `theme` object, so previews approximate the real branding.
 */
export function buildThemePreviewContext(
  theme: JourneyThemeModel | null | undefined,
): Record<string, string> {
  const context: Record<string, string> = {};
  if (!theme) return context;

  for (const [key, value] of Object.entries(theme.colors ?? {})) {
    if (typeof value === 'string') context[`theme.colors.${key}`] = value;
  }
  for (const [key, value] of Object.entries(theme.assets ?? {})) {
    if (typeof value === 'string') context[`theme.assets.${key}`] = value;
  }

  /**
   * Top-level string leaves too — `theme.logo` most of all.
   *
   * Only `colors` and `assets` were walked, so a layout reading `{{ theme.logo }}`
   * previewed with nothing there while the real send had it. `core/layout/base@1`
   * does exactly that for its brand band.
   */
  for (const [key, value] of Object.entries(theme)) {
    if (typeof value === 'string') context[`theme.${key}`] = value;
  }

  /**
   * `theme.borders` and the webfont list, as the components read them.
   *
   * Both are nested one level deeper than `colors`/`assets`, so the two loops
   * above miss them — and every `core/*` component now resolves its corner
   * radius, border style and font stack through them. Without these the canvas
   * previewed square corners and Helvetica while the real send had the brand's,
   * which is the disagreement a WYSIWYG cannot afford.
   */
  for (const [group, entries] of Object.entries(theme.borders ?? {})) {
    if (typeof entries === 'string')
      context[`theme.borders.${group}`] = entries;
  }
  for (const [name, font] of Object.entries(theme.typo?.fonts ?? {})) {
    if (!font || typeof font !== 'object') continue;
    for (const [field, value] of Object.entries(
      font as Record<string, unknown>,
    )) {
      if (typeof value === 'string') {
        context[`theme.typo.fonts.${name}.${field}`] = value;
      }
    }
  }

  /**
   * The base font, under the name the layout reads it by.
   *
   * `layout/partner_base` sets its body font from a bare `font_family`, not from
   * a `theme.*` path — the real send gets it from the email payload
   * (`Configuration::getEndUserEmailVars`), so the preview has to supply it the
   * same way or every previewed email is Helvetica whatever the theme says.
   */
  const fontFamily = theme.typo?.email?.fontFamily;
  if (typeof fontFamily === 'string' && fontFamily) {
    context.font_family = fontFamily;
  }

  return context;
}

/**
 * The journey's `email_config.template_vars` as preview paths.
 *
 * These reach the real send through the Twig payload, so a layout or partial may
 * legitimately read `email_config.template_vars.imprint` — and several do. Without
 * them the canvas renders the frame with its logo, social row and legal line
 * missing, which is a preview that quietly disagrees with what goes out.
 */
export function buildEmailConfigPreviewContext(
  templateVars: Record<string, string> | null | undefined,
): Record<string, string> {
  const context: Record<string, string> = {};
  for (const [key, value] of Object.entries(templateVars ?? {})) {
    if (typeof value === 'string') {
      context[`email_config.template_vars.${key}`] = value;
    }
  }
  return context;
}

/**
 * The configured emails **one lead source actually sends**.
 *
 * Transactional emails follow the same default-lead-source inheritance as the
 * journey configuration: a journey sends its own link for a trigger when it has
 * one, and the default journey's otherwise. So the scoped list is the journey's
 * own rows plus the default's for every trigger it has none of its own for —
 * the inherited ones keep their "Default" tag, which is what says where they
 * come from.
 *
 * Automation emails inherit the same way, but whole rather than per trigger:
 * there is no slot to match them up by, so a journey that has built none of its
 * own is a journey running the default's set-up and shows the default's mails.
 * One of its own switches the whole list over — a journey that has configured
 * its automations is not also running the default's.
 *
 * `null` (nothing selected yet) leaves both lists untouched — the cross-journey
 * overview is the honest answer before a scope is known.
 */
export function scopeConfiguredEmailRows(
  rows: ConfiguredEmailRows,
  journeyHashId: string | null,
): ConfiguredEmailRows {
  if (!journeyHashId) return rows;

  const own = rows.transactional.filter(
    (row) => row.journeyHashId === journeyHashId,
  );
  const ownTriggers = new Set(own.map((row) => row.emailType));
  const inherited = rows.transactional.filter(
    (row) =>
      row.isDefaultJourney &&
      row.journeyHashId !== journeyHashId &&
      !ownTriggers.has(row.emailType),
  );

  const ownMarketing = rows.marketing.filter(
    (row) => row.journeyHashId === journeyHashId,
  );
  // Only consulted when the journey has none of its own; the journey-id guard
  // keeps the default journey itself from listing its rows twice.
  const inheritedMarketing = rows.marketing.filter(
    (row) => row.isDefaultJourney && row.journeyHashId !== journeyHashId,
  );

  return {
    // `buildConfiguredEmailRows` already ordered both slices by trigger; the
    // default's rows land after the journey's own, which is the order they are
    // read in — what this journey set up, then what it falls back to.
    transactional: [...own, ...inherited],
    marketing: ownMarketing.length ? ownMarketing : inheritedMarketing,
  };
}
